import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { ReportData, DateRangePreset, CustomDateRange, ReportContextState, SoldItemsGrouping } from '../types/reports';
import { generateReport as fetchReportDataService } from '../services/reportsService';
import { getDateRange } from '../utils/dateUtils';
// import { auth } from '../services/firebase'; // Removed direct import
import { useAuth } from './AuthContext'; // Import useAuth

interface ReportsContextType extends ReportContextState {
  setPreset: (preset: DateRangePreset) => void;
  setCustomDate: (startDate: Date | null, endDate: Date | null) => void;
  setSoldItemsGrouping: (grouping: SoldItemsGrouping) => void;
  refreshReport: () => Promise<void>;
}

const ReportsContext = createContext<ReportsContextType | undefined>(undefined);

export const useReports = (): ReportsContextType => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error('useReports must be used within a ReportsProvider');
  }
  return context;
};

interface ReportsProviderProps {
  children: ReactNode;
}

export const ReportsProvider: React.FC<ReportsProviderProps> = ({ children }) => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>(DateRangePreset.TODAY);
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>({ startDate: null, endDate: null });
  const [soldItemsGrouping, setSoldItemsGrouping] = useState<SoldItemsGrouping>(SoldItemsGrouping.NONE);
  // const [authChecked, setAuthChecked] = useState<boolean>(false); // Remove authChecked state

  const { user, loading: authLoading } = useAuth(); // Use AuthContext's user and loading state

  // Memoize the date range to prevent infinite renders
  const { startDate: activeStartDate, endDate: activeEndDate } = useMemo(() => {
    return getDateRange(selectedPreset, customDateRange.startDate, customDateRange.endDate);
  }, [selectedPreset, customDateRange.startDate, customDateRange.endDate]);

  // Check auth state when component mounts
  // useEffect(() => { // This useEffect is removed
  //   const checkAuth = () => {
  //     setAuthChecked(true);
  //   };
  //   const unsubscribe = auth?.onAuthStateChanged?.(checkAuth) || (() => {});
  //   return () => unsubscribe();
  // }, []);

  const loadReportData = useCallback(async (start: Date, end: Date) => {
    // if (!authChecked) return; // Remove this check based on authChecked

    if (!user) { // Check user from useAuth()
      setError("Raporları görüntülemek için lütfen giriş yapın.");
      setReportData(null);
      setLoading(false); // Ensure loading state is false if returning early due to no user
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchReportDataService(start, end);
      setReportData(data);
      
      // If no sold items were found, set a more user-friendly message
      if (data && data.soldItems.length === 0 && data.totalRepairIncomeInTry === 0) { // Also check repair income
        setError("Seçili dönemde herhangi bir satış veya tamamlanmış tamirat bulunmamaktadır.");
      } else if (data && data.soldItems.length === 0 && data.totalRepairIncomeInTry > 0) {
        setError("Seçili dönemde satılan ürün bulunmamaktadır, ancak tamirat geliri mevcuttur.");
      }


    } catch (err: any) {
      setError(err.message || 'Rapor verileri alınamadı.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [user]); // Update dependency array for useCallback

  useEffect(() => {
    if (!authLoading) { // Proceed only if auth state determination is complete
      if (user) { // If user is authenticated
        loadReportData(activeStartDate, activeEndDate);
      } else { // If not authenticated after auth state is resolved
        setError("Raporları görüntülemek için lütfen giriş yapın.");
        setReportData(null);
        setLoading(false); // Ensure loading is false as no data will be fetched
      }
    }
    // If authLoading is true, wait for AuthContext to resolve user state
  }, [user, authLoading, activeStartDate, activeEndDate, loadReportData]); // Update dependencies for useEffect

  const setPreset = (preset: DateRangePreset) => {
    setSelectedPreset(preset);
    
    // Reset custom date range when switching to a preset
    if (preset !== DateRangePreset.CUSTOM) {
      setCustomDateRange({ startDate: null, endDate: null });
    }
  };

  const setCustomDate = (startDate: Date | null, endDate: Date | null) => {
    setCustomDateRange({ startDate, endDate });
    if (selectedPreset !== DateRangePreset.CUSTOM) {
      setSelectedPreset(DateRangePreset.CUSTOM);
    }
  };

  const refreshReport = async () => {
    await loadReportData(activeStartDate, activeEndDate);
  };

  const value: ReportsContextType = {
    reportData,
    loading,
    error,
    selectedPreset,
    customDateRange,
    activeStartDate,
    activeEndDate,
    soldItemsGrouping,
    setPreset,
    setCustomDate,
    setSoldItemsGrouping,
    refreshReport,
  };

  return (
    <ReportsContext.Provider value={value}>
      {children}
    </ReportsContext.Provider>
  );
};

export default ReportsContext;